/**
 * JIAVON listicle (sections/jiavon-listicle.liquid), October 28, 2026.
 *
 * Signup: posts the email to Klaviyo's client subscriptions endpoint with the
 * public site ID into the JIAVON Early Access list, and shows success only on
 * Klaviyo's 202 Accepted. The signup carries the page, form location and any
 * UTM parameters as profile properties.
 *
 * Also: jump links that scroll to and focus the next form, the sticky mobile
 * button, the tap to play film, and Shopify customer events for views, button
 * clicks, film plays and confirmed signups. Only a 202 from Klaviyo produces
 * jiavon_signup_success; clicks and plays never do. No email address is ever
 * sent to analytics or written to a URL.
 */
(() => {
  const root = document.querySelector('[data-jl]');
  if (!root) return;

  const ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  const REVISION = '2026-07-15';
  const TIMEOUT_MS = 15000;
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const MESSAGES = {
    required: 'Please enter your email address.',
    invalid: 'Please enter a valid email address, like name@example.com.',
    rejected: "That email address didn't go through. Please check it and try again.",
    rateLimited: 'Too many tries in a row. Please wait a moment and try again.',
    provider: "We couldn't add you just now. Please try again, or email hello@jiavon.com and we'll add you.",
    network: "We couldn't connect. Check your connection and try again, or email hello@jiavon.com.",
    config: "Signup isn't available right now. Please email hello@jiavon.com and we'll add you.",
    loading: 'Joining…',
  };

  const config = root.dataset;
  const utm = readUtm();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const forms = [...root.querySelectorAll('[data-jl-form]')];
  const sticky = root.querySelector('[data-jl-sticky]');
  const footer = root.querySelector('[data-jl-footer]');
  let joined = false;

  /** UTM values from the landing URL, kept for the session so they survive in page jumps and reloads. */
  function readUtm() {
    const params = new URLSearchParams(window.location.search);
    let values = {};
    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value) values[key] = value.trim().slice(0, 100);
    }
    try {
      if (Object.keys(values).length) {
        sessionStorage.setItem('jiavon_utm', JSON.stringify(values));
      } else {
        values = JSON.parse(sessionStorage.getItem('jiavon_utm') || '{}');
      }
    } catch (error) {
      // Storage can be blocked (private mode); the URL values still apply.
    }
    return values;
  }

  /**
   * Publishes a Shopify customer event. Pixels receive it under the store's
   * existing consent settings. Payloads never include the email address.
   * @param {string} name
   * @param {Record<string, string | number>} data
   */
  function track(name, data = {}) {
    const payload = { page: config.page, ...utm, ...data };
    const publish = () => {
      try {
        window.Shopify?.analytics?.publish?.(name, payload);
      } catch (error) {
        // Analytics must never break the page.
      }
    };
    if (window.Shopify?.analytics?.publish) publish();
    else window.addEventListener('load', publish, { once: true });
  }

  /** @param {string} email @param {string} location */
  function subscriptionBody(email, location) {
    const properties = {
      signup_source: config.signupTag,
      signup_location: location,
      signup_page: config.page,
      ...utm,
      // First touch from snippets/jiavon-attribution.liquid, shared with the homepage.
      ...(window.jiavonAttribution?.firstTouch?.() || {}),
    };
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: config.customSource,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                properties,
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
              },
            },
          },
        },
        relationships: { list: { data: { type: 'list', id: config.listId } } },
      },
    };
  }

  /** @param {HTMLElement} wrap */
  function parts(wrap) {
    const form = /** @type {HTMLFormElement} */ (wrap.querySelector('form'));
    return {
      form,
      input: /** @type {HTMLInputElement} */ (form.querySelector('input[type="email"]')),
      trap: /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="company"]')),
      message: /** @type {HTMLElement} */ (form.querySelector('.jl-form__msg')),
      label: /** @type {HTMLElement} */ (form.querySelector('[data-jl-submit-label]')),
      success: /** @type {HTMLElement} */ (wrap.querySelector('[data-jl-success]')),
    };
  }

  function showError(wrap, text) {
    const { input, message } = parts(wrap);
    message.textContent = text;
    message.classList.add('is-error');
    input.setAttribute('aria-invalid', 'true');
    input.focus();
  }

  function clearMessage(wrap) {
    const { input, message } = parts(wrap);
    message.textContent = '';
    message.classList.remove('is-error');
    input.removeAttribute('aria-invalid');
  }

  function setLoading(wrap, loading) {
    const { form, label } = parts(wrap);
    if (loading) {
      wrap.dataset.state = 'loading';
      label.dataset.idle = label.textContent;
      label.textContent = MESSAGES.loading;
      form.setAttribute('aria-busy', 'true');
    } else {
      delete wrap.dataset.state;
      if (label.dataset.idle) label.textContent = label.dataset.idle;
      form.removeAttribute('aria-busy');
    }
  }

  /**
   * Shows the success panel in every form, so nobody is asked twice, and focuses the one used.
   * signup_id is random, carries no personal data, and lets a pixel deduplicate its conversion.
   */
  function succeed(wrap) {
    joined = true;
    for (const other of forms) other.classList.add('is-joined');
    parts(wrap).success.focus();
    const signupId = window.crypto?.randomUUID?.() || `${Date.now()}${Math.random().toString(36).slice(2)}`;
    track('jiavon_signup_success', { location: wrap.dataset.location, signup_id: signupId });
    updateSticky();
  }

  /** @param {Response} response */
  async function errorDetail(response) {
    try {
      const body = await response.json();
      return (body.errors || []).map((error) => `${error.code} ${error.detail}`).join(' ');
    } catch (error) {
      return '';
    }
  }

  /** @param {HTMLElement} wrap */
  async function submit(wrap) {
    if (wrap.dataset.state === 'loading' || joined) return;
    const { input, trap } = parts(wrap);
    if (trap && trap.value) return;

    const email = input.value.trim();
    if (!email) return showError(wrap, MESSAGES.required);
    if (!EMAIL_PATTERN.test(email)) return showError(wrap, MESSAGES.invalid);
    if (!config.companyId || !config.listId) return showError(wrap, MESSAGES.config);

    clearMessage(wrap);
    setLoading(wrap, true);
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), TIMEOUT_MS);

    try {
      const response = await fetch(`${ENDPOINT}?company_id=${encodeURIComponent(config.companyId)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          Accept: 'application/vnd.api+json',
          revision: REVISION,
        },
        body: JSON.stringify(subscriptionBody(email, wrap.dataset.location || '')),
        signal: controller.signal,
      });

      if (response.status === 202 || response.ok) {
        setLoading(wrap, false);
        succeed(wrap);
        return;
      }

      const detail = await errorDetail(response);
      setLoading(wrap, false);
      if (response.status === 429) showError(wrap, MESSAGES.rateLimited);
      else if (response.status < 500 && /email/i.test(detail)) showError(wrap, MESSAGES.rejected);
      else showError(wrap, MESSAGES.provider);
      track('jiavon_signup_error', { location: wrap.dataset.location, status: response.status });
    } catch (error) {
      setLoading(wrap, false);
      showError(wrap, MESSAGES.network);
      track('jiavon_signup_error', { location: wrap.dataset.location, status: 0 });
    } finally {
      window.clearTimeout(timer);
    }
  }

  /** The first form not entirely above the viewport, otherwise the last one. */
  function nextForm() {
    return forms.find((wrap) => wrap.getBoundingClientRect().bottom > 0) || forms[forms.length - 1];
  }

  /**
   * Scrolls to a form and focuses its email field. data-jl-jump="exact" goes to
   * the form the link names; otherwise it goes to the next form down the page.
   * @param {MouseEvent} event
   */
  function jump(event) {
    const link = /** @type {HTMLAnchorElement} */ (event.currentTarget);
    event.preventDefault();
    track('jiavon_signup_click', { location: link.dataset.location, action: 'jump' });
    const named = link.dataset.jlJump === 'exact' ? forms.find((wrap) => `#${wrap.id}` === link.hash) : null;
    const target = named || nextForm();
    if (!target) return;
    target.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
    if (target.classList.contains('is-joined')) return;
    parts(target).input.focus({ preventScroll: true });
  }

  /**
   * The hero film: a poster until someone taps play, so no video downloads before
   * then. It starts muted with a sound button, plays once and pauses when scrolled away.
   * @param {HTMLElement} wrap
   */
  function setupFilm(wrap) {
    const video = /** @type {HTMLVideoElement} */ (wrap.querySelector('video'));
    const start = /** @type {HTMLButtonElement} */ (wrap.querySelector('[data-jl-film-start]'));
    const controls = /** @type {HTMLElement} */ (wrap.querySelector('[data-jl-film-controls]'));
    const toggle = /** @type {HTMLButtonElement} */ (wrap.querySelector('[data-jl-film-toggle]'));
    const sound = /** @type {HTMLButtonElement} */ (wrap.querySelector('[data-jl-film-sound]'));
    let tracked = false;

    function sync() {
      const playing = !video.paused && !video.ended;
      wrap.classList.toggle('is-playing', playing);
      wrap.classList.toggle('has-sound', !video.muted);
      toggle.setAttribute('aria-label', playing ? 'Pause the film' : video.ended ? 'Replay the film' : 'Play the film');
      sound.setAttribute('aria-label', video.muted ? 'Turn sound on' : 'Turn sound off');
      sound.setAttribute('aria-pressed', String(!video.muted));
    }

    function play() {
      if (!video.src) {
        const width = video.parentElement.clientWidth * (window.devicePixelRatio || 1);
        video.src = width > 800 ? video.dataset.srcLarge : video.dataset.srcSmall;
      }
      if (video.ended) video.currentTime = 0;
      video.play()?.catch(() => sync());
      if (!tracked) {
        tracked = true;
        track('jiavon_film_play', { location: 'film' });
      }
    }

    start.addEventListener('click', () => {
      video.hidden = false;
      controls.hidden = false;
      start.hidden = true;
      video.muted = true;
      play();
      toggle.focus({ preventScroll: true });
    });
    toggle.addEventListener('click', () => (video.paused || video.ended ? play() : video.pause()));
    sound.addEventListener('click', () => {
      video.muted = !video.muted;
      if (video.paused) play();
      sync();
    });
    video.addEventListener('playing', () => wrap.classList.add('is-ready'));
    for (const type of ['play', 'pause', 'ended', 'volumechange']) video.addEventListener(type, sync);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting && !video.paused) video.pause();
        }
      }, { threshold: 0.25 }).observe(wrap);
    }
  }

  const film = root.querySelector('[data-jl-film]');
  if (film) setupFilm(/** @type {HTMLElement} */ (film));

  /* Sticky button: hidden while any form or the footer is on screen, and after joining. */
  const onScreen = new Set();
  function updateSticky() {
    if (!sticky) return;
    sticky.classList.toggle('is-visible', !joined && onScreen.size === 0);
  }

  for (const wrap of forms) {
    const { form, input } = parts(wrap);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      submit(wrap);
    });
    form.querySelector('[data-jl-submit]')?.addEventListener('click', () => {
      track('jiavon_signup_click', { location: wrap.dataset.location, action: 'submit' });
    });
    input.addEventListener('input', () => {
      if (input.hasAttribute('aria-invalid')) clearMessage(wrap);
    });
  }

  for (const link of root.querySelectorAll('[data-jl-jump]')) {
    link.addEventListener('click', jump);
  }

  if (sticky && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) onScreen.add(entry.target);
        else onScreen.delete(entry.target);
      }
      updateSticky();
    });
    for (const wrap of forms) observer.observe(wrap);
    if (footer) observer.observe(footer);
  }

  track('jiavon_listicle_view');
})();
